import { Fragment, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiCall, ApiError } from '../utils/apiHelpers';
import type { AuditDoc } from '../types';

export function AuditPage() {
  const { industryId } = useParams<{ industryId: string }>();
  const [entries, setEntries] = useState<AuditDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!industryId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await apiCall<AuditDoc[]>(`/api/industries/${industryId}/audit`);
        if (cancelled) return;
        setEntries(data);
      } catch (e) {
        if (!cancelled) setError(e instanceof ApiError ? e.message : (e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [industryId]);

  function toggle(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  if (!industryId) {
    return <p style={{ color: 'var(--danger-color)' }}>Missing industry id.</p>;
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Link
          to={`/industries/${industryId}/schemas`}
          style={{ color: 'var(--text-muted)', fontSize: 13, textDecoration: 'none' }}
        >
          ← Schemas
        </Link>
        <h1 style={{ margin: '4px 0 0' }}>Audit log</h1>
      </div>

      {error && (
        <div style={{ padding: 12, background: '#fee', border: '1px solid #fcc', borderRadius: 6, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {loading ? (
        <p>Loading…</p>
      ) : entries.length === 0 ? (
        <p style={{ color: 'var(--text-muted)' }}>No audit entries yet.</p>
      ) : (
        <div
          style={{
            border: '1px solid var(--border-color)',
            borderRadius: 8,
            background: 'var(--card-bg)',
            overflowX: 'auto',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border-color)' }}>
                <th style={{ padding: 12, width: 1, whiteSpace: 'nowrap' }}>Timestamp</th>
                <th style={{ padding: 12 }}>Actor</th>
                <th style={{ padding: 12 }}>Action</th>
                <th style={{ padding: 12 }}>Resource</th>
                <th style={{ padding: 12 }}></th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => {
                const isOpen = expanded.has(e.id);
                return (
                  <Fragment key={e.id}>
                    <tr
                      style={{ borderTop: '1px solid var(--border-color)', cursor: 'pointer' }}
                      onClick={() => toggle(e.id)}
                    >
                      <td style={{ padding: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap', fontSize: 12 }}>
                        {new Date(e.timestamp).toLocaleString()}
                      </td>
                      <td style={{ padding: 12 }}>
                        <div>{e.actorEmail ?? '(system)'}</div>
                        {e.actorObjectId && (
                          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{e.actorObjectId}</div>
                        )}
                      </td>
                      <td style={{ padding: 12 }}>
                        <code
                          style={{
                            background: 'var(--bg-color, #f4f4f4)',
                            padding: '2px 6px',
                            borderRadius: 4,
                            fontSize: 12,
                          }}
                        >
                          {e.action}
                        </code>
                      </td>
                      <td style={{ padding: 12, color: 'var(--text-muted)', fontSize: 13 }}>
                        {e.resourceType}:{e.resourceId}
                      </td>
                      <td style={{ padding: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                        {isOpen ? '▼' : '▶'}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr style={{ background: 'var(--bg-color, #fafafa)' }}>
                        <td colSpan={5} style={{ padding: 12 }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                            <div>
                              <strong style={{ fontSize: 12 }}>Before</strong>
                              <pre
                                style={{
                                  background: 'var(--card-bg)',
                                  border: '1px solid var(--border-color)',
                                  borderRadius: 4,
                                  padding: 8,
                                  fontSize: 11,
                                  overflowX: 'auto',
                                  margin: 0,
                                }}
                              >
                                {e.before === undefined ? '(none)' : JSON.stringify(e.before, null, 2)}
                              </pre>
                            </div>
                            <div>
                              <strong style={{ fontSize: 12 }}>After</strong>
                              <pre
                                style={{
                                  background: 'var(--card-bg)',
                                  border: '1px solid var(--border-color)',
                                  borderRadius: 4,
                                  padding: 8,
                                  fontSize: 11,
                                  overflowX: 'auto',
                                  margin: 0,
                                }}
                              >
                                {e.after === undefined ? '(none)' : JSON.stringify(e.after, null, 2)}
                              </pre>
                            </div>
                          </div>
                          {e.metadata && Object.keys(e.metadata).length > 0 && (
                            <div style={{ marginTop: 8 }}>
                              <strong style={{ fontSize: 12 }}>Metadata</strong>
                              <pre
                                style={{
                                  background: 'var(--card-bg)',
                                  border: '1px solid var(--border-color)',
                                  borderRadius: 4,
                                  padding: 8,
                                  fontSize: 11,
                                  overflowX: 'auto',
                                  margin: 0,
                                }}
                              >
                                {JSON.stringify(e.metadata, null, 2)}
                              </pre>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
